import { useCallback, useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import {
    ArrowLeft,
    Upload,
    FileText,
    Check,
    X,
    Trash2,
    Send,
    ClipboardList,
    Stethoscope,
} from "lucide-react";
import { api } from "@/lib/api";
import { TID } from "@/constants/testIds";

const STATUS_LABEL = {
    suggested: { label: "Suggested by Vaidya", cls: "border-veda-brand/40 bg-veda-brand/[0.06] text-veda-brand" },
    requested: { label: "You requested — awaiting Vaidya", cls: "border-veda-border bg-veda-surface2 text-veda-onSurface" },
    done: { label: "Done", cls: "border-veda-brand bg-veda-brand text-white" },
    declined: { label: "Declined", cls: "border-veda-border bg-veda-surface2 text-veda-muted line-through" },
};

async function fileToDataUrl(file) {
    return new Promise((res, rej) => {
        const r = new FileReader();
        r.onload = () => res(r.result);
        r.onerror = rej;
        r.readAsDataURL(file);
    });
}

function ReportPill({ report, suggestionId }) {
    const [loading, setLoading] = useState(false);
    async function open() {
        setLoading(true);
        try {
            const { data } = await api.get(
                `/test-suggestions/${suggestionId}/report/${report.report_id}`,
            );
            const w = window.open();
            if (w && data?.data_url) {
                if ((data.mime_type || "").startsWith("image/")) {
                    w.document.write(
                        `<img src="${data.data_url}" style="max-width:100%;height:auto"/>`,
                    );
                } else if (data.mime_type === "application/pdf") {
                    w.document.write(
                        `<iframe src="${data.data_url}" style="border:0;width:100vw;height:100vh"></iframe>`,
                    );
                } else {
                    w.document.write(
                        `<a href="${data.data_url}" download="${data.filename}">Download ${data.filename}</a>`,
                    );
                }
            }
        } catch {
            toast.error("Could not open report.");
        } finally {
            setLoading(false);
        }
    }
    return (
        <button
            type="button"
            onClick={open}
            data-testid={TID.testsReport(report.report_id)}
            disabled={loading}
            className="inline-flex items-center gap-1 rounded-pill border border-veda-border bg-veda-surface2 px-2.5 py-1 text-[11px] font-medium text-veda-onSurface hover:border-veda-brand hover:text-veda-brand"
        >
            <FileText size={11} />
            <span className="max-w-[140px] truncate">{report.filename}</span>
        </button>
    );
}

function RequestForm({ onCreated, presets }) {
    const [name, setName] = useState("");
    const [notes, setNotes] = useState("");
    const [saving, setSaving] = useState(false);

    async function submit(e) {
        e.preventDefault();
        const n = name.trim();
        if (!n) {
            toast.error("Pick a test or type one.");
            return;
        }
        setSaving(true);
        try {
            await api.post("/patient/test-requests", { test_name: n, notes: notes.trim() || null });
            setName("");
            setNotes("");
            toast.success("Test requested. Your Vaidya will review.");
            onCreated?.();
        } catch (err) {
            const d = err?.response?.data?.detail;
            toast.error(typeof d === "string" ? d : "Could not request.");
        } finally {
            setSaving(false);
        }
    }

    return (
        <form
            onSubmit={submit}
            data-testid={TID.testsRequestForm}
            className="grid gap-3 border border-veda-border bg-veda-surface p-4"
        >
            <div className="flex flex-wrap items-start gap-2">
                <label className="min-w-[220px] flex-1">
                    <span className="text-[10px] font-semibold uppercase tracking-[0.18em] text-veda-muted">
                        Test name
                    </span>
                    <input
                        type="text"
                        value={name}
                        onChange={(e) => setName(e.target.value)}
                        placeholder="CBC, Vitamin D, HbA1c…"
                        data-testid={TID.testsRequestName}
                        className="mt-1.5 w-full border border-veda-border bg-veda-surface px-3 py-2 text-sm focus:border-veda-brand focus:outline-none"
                    />
                </label>
                <label className="min-w-[220px] flex-1">
                    <span className="text-[10px] font-semibold uppercase tracking-[0.18em] text-veda-muted">
                        Why? (optional)
                    </span>
                    <input
                        type="text"
                        value={notes}
                        onChange={(e) => setNotes(e.target.value)}
                        placeholder="Symptoms or reason"
                        data-testid={TID.testsRequestNotes}
                        className="mt-1.5 w-full border border-veda-border bg-veda-surface px-3 py-2 text-sm focus:border-veda-brand focus:outline-none"
                    />
                </label>
                <button
                    type="submit"
                    disabled={saving}
                    data-testid={TID.testsRequestSubmit}
                    className="mt-6 inline-flex items-center gap-2 bg-veda-inverse px-4 py-2 text-[10px] font-semibold uppercase tracking-[0.14em] text-veda-onInverse disabled:opacity-60"
                >
                    <Send size={12} /> {saving ? "Requesting…" : "Request"}
                </button>
            </div>
            {presets.length > 0 && (
                <div>
                    <p className="text-[10px] font-semibold uppercase tracking-[0.18em] text-veda-muted">
                        Quick pick
                    </p>
                    <div className="mt-1.5 flex flex-wrap gap-1.5">
                        {presets.map((p) => (
                            <button
                                type="button"
                                key={p}
                                onClick={() => setName(p)}
                                data-testid={TID.testsPresetChip(p)}
                                className="rounded-pill border border-veda-border bg-veda-surface2 px-2.5 py-0.5 text-[11px] font-semibold text-veda-onSurface hover:border-veda-brand hover:text-veda-brand"
                            >
                                {p}
                            </button>
                        ))}
                    </div>
                </div>
            )}
        </form>
    );
}

function TestItem({ item, onChanged }) {
    const [busy, setBusy] = useState(false);
    const fileInput = useRef(null);

    async function upload(e) {
        const f = e.target.files?.[0];
        if (!f) return;
        if (f.size > 4 * 1024 * 1024) {
            toast.error("File too large (max 4 MB).");
            return;
        }
        setBusy(true);
        try {
            const data_url = await fileToDataUrl(f);
            await api.patch(`/test-suggestions/${item.suggestion_id}`, {
                add_report: {
                    filename: f.name,
                    mime_type: f.type || "application/octet-stream",
                    data_url,
                },
            });
            toast.success("Report uploaded.");
            onChanged?.();
        } catch (err) {
            const d = err?.response?.data?.detail;
            toast.error(typeof d === "string" ? d : "Could not upload.");
        } finally {
            setBusy(false);
            if (fileInput.current) fileInput.current.value = "";
        }
    }

    async function setStatus(status) {
        setBusy(true);
        try {
            await api.patch(`/test-suggestions/${item.suggestion_id}`, { status });
            toast.success(status === "done" ? "Marked done." : `Marked ${status}.`);
            onChanged?.();
        } catch (err) {
            const d = err?.response?.data?.detail;
            toast.error(typeof d === "string" ? d : "Could not update.");
        } finally {
            setBusy(false);
        }
    }

    async function del() {
        if (!window.confirm("Delete this test?")) return;
        setBusy(true);
        try {
            await api.delete(`/test-suggestions/${item.suggestion_id}`);
            toast.success("Deleted.");
            onChanged?.();
        } catch {
            toast.error("Could not delete.");
        } finally {
            setBusy(false);
        }
    }

    const s = STATUS_LABEL[item.status] || STATUS_LABEL.suggested;

    return (
        <li
            data-testid={TID.testsItem(item.suggestion_id)}
            className="border border-veda-border bg-veda-surface p-4"
        >
            <div className="flex flex-wrap items-start justify-between gap-2">
                <div className="min-w-0 flex-1">
                    <div className="flex items-baseline gap-2">
                        <p className="font-display text-lg text-veda-onSurface">
                            {item.test_name}
                        </p>
                        <span
                            className={`rounded-pill border px-2 py-0.5 text-[10px] font-semibold uppercase tracking-[0.12em] ${s.cls}`}
                        >
                            {s.label}
                        </span>
                    </div>
                    {item.notes && (
                        <p className="mt-1 text-sm italic text-veda-muted">
                            {item.notes}
                        </p>
                    )}
                    <p className="mt-1 text-[10px] text-veda-muted">
                        {item.requested_by === "doctor" ? "Recommended" : "Requested by you"} ·{" "}
                        {new Date(item.created_at).toLocaleDateString()}
                        {item.done_at && ` · done ${new Date(item.done_at).toLocaleDateString()}`}
                    </p>
                </div>
                <button
                    type="button"
                    onClick={del}
                    data-testid={TID.testsDelete(item.suggestion_id)}
                    disabled={busy}
                    className="grid h-7 w-7 place-items-center text-veda-muted hover:text-veda-error"
                    aria-label="Delete"
                >
                    <Trash2 size={13} />
                </button>
            </div>

            {(item.reports || []).length > 0 && (
                <div className="mt-3 flex flex-wrap gap-1.5">
                    {item.reports.map((r) => (
                        <ReportPill
                            key={r.report_id}
                            report={r}
                            suggestionId={item.suggestion_id}
                        />
                    ))}
                </div>
            )}

            <div className="mt-3 flex flex-wrap items-center gap-2">
                <label
                    className={`inline-flex cursor-pointer items-center gap-1 border border-veda-border bg-veda-surface2 px-3 py-1.5 text-[10px] font-semibold uppercase tracking-[0.14em] text-veda-onSurface hover:border-veda-brand hover:text-veda-brand ${
                        busy ? "opacity-60" : ""
                    }`}
                >
                    <Upload size={11} />
                    Upload report
                    <input
                        ref={fileInput}
                        type="file"
                        accept="image/*,application/pdf"
                        className="hidden"
                        onChange={upload}
                        disabled={busy}
                        data-testid={TID.testsUpload(item.suggestion_id)}
                    />
                </label>
                {item.status !== "done" && (
                    <button
                        type="button"
                        onClick={() => setStatus("done")}
                        disabled={busy}
                        data-testid={TID.testsMarkDone(item.suggestion_id)}
                        className="inline-flex items-center gap-1 border border-veda-brand bg-veda-brand px-3 py-1.5 text-[10px] font-semibold uppercase tracking-[0.14em] text-white disabled:opacity-60"
                    >
                        <Check size={11} /> Mark done
                    </button>
                )}
                {item.status !== "declined" && item.requested_by === "doctor" && (
                    <button
                        type="button"
                        onClick={() => setStatus("declined")}
                        disabled={busy}
                        data-testid={TID.testsMarkDeclined(item.suggestion_id)}
                        className="inline-flex items-center gap-1 border border-veda-border bg-veda-surface px-3 py-1.5 text-[10px] font-semibold uppercase tracking-[0.14em] text-veda-muted hover:border-veda-error hover:text-veda-error disabled:opacity-60"
                    >
                        <X size={11} /> Decline
                    </button>
                )}
            </div>
        </li>
    );
}

export default function Tests() {
    const [items, setItems] = useState([]);
    const [presets, setPresets] = useState([]);
    const [loading, setLoading] = useState(true);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const [tests, pres] = await Promise.all([
                api.get("/patient/test-suggestions"),
                api.get("/test-suggestions/presets"),
            ]);
            setItems(tests.data || []);
            setPresets(pres.data?.presets || []);
        } catch {
            toast.error("Could not load tests.");
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        load();
    }, [load]);

    const suggested = items.filter((i) => i.status === "suggested");
    const requested = items.filter((i) => i.status === "requested");
    const done = items.filter((i) => i.status === "done");
    const declined = items.filter((i) => i.status === "declined");

    return (
        <div data-testid={TID.testsPage} className="pb-16">
            <div className="border-b border-veda-border">
                <div className="veda-container py-6 md:py-10">
                    <Link
                        to="/dashboard"
                        className="inline-flex items-center gap-1 text-xs font-medium text-veda-muted hover:text-veda-onSurface"
                    >
                        <ArrowLeft size={13} /> Dashboard
                    </Link>
                    <div className="mt-4">
                        <p className="text-[10px] font-semibold uppercase tracking-[0.24em] text-veda-brand">
                            Lab tests
                        </p>
                        <h1 className="font-display mt-1 text-3xl text-veda-onSurface md:text-5xl">
                            Tests suggested to you, and ones you'd like reviewed.
                        </h1>
                        <p className="mt-2 max-w-xl text-sm text-veda-muted">
                            Upload your report as soon as it's ready — your
                            Vaidya sees it before the next consult.
                        </p>
                    </div>
                </div>
            </div>

            <div className="veda-container py-8 md:py-12 space-y-8">
                <section>
                    <div className="mb-3 flex items-center gap-2">
                        <span className="grid h-8 w-8 place-items-center bg-veda-brand/10 text-veda-brand">
                            <Send size={14} />
                        </span>
                        <div>
                            <p className="text-[10px] font-semibold uppercase tracking-[0.22em] text-veda-muted">
                                Ask for a test
                            </p>
                            <p className="font-display text-lg text-veda-onSurface">
                                Want your Vaidya to review a test?
                            </p>
                        </div>
                    </div>
                    <RequestForm onCreated={load} presets={presets} />
                </section>

                {loading && items.length === 0 && (
                    <p className="text-sm text-veda-muted">Loading…</p>
                )}

                {suggested.length > 0 && (
                    <Group
                        icon={Stethoscope}
                        title="Recommended by your Vaidya"
                        items={suggested}
                        onChanged={load}
                    />
                )}
                {requested.length > 0 && (
                    <Group
                        icon={ClipboardList}
                        title="Awaiting review"
                        items={requested}
                        onChanged={load}
                    />
                )}
                {done.length > 0 && (
                    <Group
                        icon={Check}
                        title="Done"
                        items={done}
                        onChanged={load}
                    />
                )}
                {declined.length > 0 && (
                    <Group
                        icon={X}
                        title="Declined"
                        items={declined}
                        onChanged={load}
                    />
                )}
                {!loading && items.length === 0 && (
                    <div className="border border-dashed border-veda-border bg-veda-surface2/40 p-8 text-center text-sm text-veda-muted">
                        Nothing here yet. When your Vaidya suggests a test — or
                        you ask for one — it will show up here.
                    </div>
                )}
            </div>
        </div>
    );
}

function Group({ icon: Icon, title, items, onChanged }) {
    return (
        <section>
            <div className="mb-3 flex items-center gap-2">
                <span className="grid h-8 w-8 place-items-center bg-veda-brand/10 text-veda-brand">
                    <Icon size={14} />
                </span>
                <p className="font-display text-lg text-veda-onSurface">
                    {title}{" "}
                    <span className="text-veda-muted">({items.length})</span>
                </p>
            </div>
            <ul className="space-y-2">
                {items.map((i) => (
                    <TestItem
                        key={i.suggestion_id}
                        item={i}
                        onChanged={onChanged}
                    />
                ))}
            </ul>
        </section>
    );
}
